import { useContext } from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes } from 'react-router-dom';
import ActivatAccountLandingPage from '../../components/user_management/activate-account';
import { EditUserProfile } from '../../components/user_management/edit-user-profile';
import SignUpFollowupPage from '../../components/user_management/email-confirmation';
import AuthenticationContext from '../../contexts/authentication.context';
import { AppRoutes } from '../../enums';
import { AuthLayout, CompleteLayout } from '../../layouts';
import authService from '../../services/auth.service';
import { IAuthState } from '../../types';
import { IsUserRedirect, ProtectedRoute } from '../../utils/routes';
import { NotFoundRoute } from '../404-view';
import { CandidatesList } from '../dashboard/candidates/candidates-list';
import JobBoard from '../dashboard/candidates/job-board';
import { CompanyView } from '../dashboard/companies/company-view';
import { AddJobSection } from '../dashboard/jobs/add-job-section';
import { EditJobPost } from '../dashboard/jobs/edit-job-post';
import { JobView } from '../dashboard/jobs/job-view';
import { JobsDashboard } from '../dashboard/jobs/jobs-dashboard';
import { OffersDashboard } from '../dashboard/jobs/offers-dashboard';
import TeamMembers from '../dashboard/team-members/team-members';
import FAQView from '../FAQView';
import PaymentDetailsDashboard from '../payment-details/payment-details-dashboard';
import ChangePasswordView from '../user_management/change-password-view';
import { EditCompanyProfile } from '../user_management/edit-company-profile';
import ForgetPasswordView from '../user_management/forget-password-view';
import InvitationSignup from '../user_management/invitation-signup';
import ResetPasswordView from '../user_management/reset-password-view';
import { SignIn } from '../user_management/signin';
import { SignInTempAccess } from '../user_management/signin-temp-access';
import { SignUp } from '../user_management/signup';
import TermsOfUseView from '../user_management/terms-of-use/terms-of-use';
import { OutsourcingDashboard, PerksDashboard } from '../../pages';

export const AppRouter: React.FC<{ authState: IAuthState }> = ({ authState }) => {
	const authContext = useContext(AuthenticationContext);
	const { user, setUser } = authContext;
	return (
		<Router>
			<Routes>
				<Route
					path={AppRoutes.PERKS}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.PERKS}
						element={
							<CompleteLayout>
								<PerksDashboard />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.OUTSOURCING}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.OUTSOURCING}
						element={
							<CompleteLayout>
								<OutsourcingDashboard />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.JOBS_DASHBOARD}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.JOBS_DASHBOARD}
						element={
							<CompleteLayout>
								<JobsDashboard />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.JOB_BOARD}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.JOB_BOARD}
						element={
							<CompleteLayout>
								<JobBoard />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.JOB_BOARD_2}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.JOB_BOARD_2}
						element={
							<CompleteLayout>
								<JobBoard />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.CANDIDATES_DASHBOARD}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.CANDIDATES_DASHBOARD}
						element={
							<CompleteLayout>
								<CandidatesList />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.OFFERS_DASHBOARD}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.OFFERS_DASHBOARD}
						element={
							<CompleteLayout>
								<OffersDashboard />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.JOB_PROFILE}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.JOB_PROFILE}
						element={
							<CompleteLayout>
								<JobView />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.JOB_EDIT}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.JOB_EDIT}
						element={
							<CompleteLayout>
								<EditJobPost />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.PAYMENT_DETAILS}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.PAYMENT_DETAILS}
						element={
							<CompleteLayout>
								<PaymentDetailsDashboard user={user} />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.FAQ_DASHBOARD}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.FAQ_DASHBOARD}
						element={
							<CompleteLayout>
								<FAQView user={user} />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.ADD_JOB}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.ADD_JOB}
						element={
							<CompleteLayout>
								<AddJobSection />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.EDIT_USER_PROFILE}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.EDIT_USER_PROFILE}
						element={
							<CompleteLayout>
								<EditUserProfile setUser={setUser} />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.COMPANY_PROFILE}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.COMPANY_PROFILE}
						element={
							<CompleteLayout>
								<CompanyView />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.COMPANY_PROFILE_2}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.COMPANY_PROFILE_2}
						element={
							<CompleteLayout>
								<CompanyView />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.TEAM_MEMBERS}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.TEAM_MEMBERS}
						element={
							<CompleteLayout>
								<TeamMembers />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.EDIT_COMPANY_PROFILE}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.EDIT_COMPANY_PROFILE}
						element={
							<CompleteLayout>
								<EditCompanyProfile setUser={setUser} />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.EDIT_COMPANY_PROFILE_2}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.EDIT_COMPANY_PROFILE_2}
						element={
							<CompleteLayout>
								<EditCompanyProfile setUser={setUser} />
							</CompleteLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.SIGNIN}
					element={
						<IsUserRedirect
							allowed={authService.isAuthenticated(authState.token)}
							loggedInPath={AppRoutes.ROOT}
						/>
					}
				>
					<Route
						path={AppRoutes.SIGNIN}
						element={
							<AuthLayout>
								<SignIn />
							</AuthLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.SIGNIN_TEMP_ACCESS}
					element={
						<IsUserRedirect
							allowed={authService.isAuthenticated(authState.token)}
							loggedInPath={AppRoutes.ROOT}
						/>
					}
				>
					<Route
						path={AppRoutes.SIGNIN_TEMP_ACCESS}
						element={
							<AuthLayout>
								<SignInTempAccess />
							</AuthLayout>
						}
					/>
				</Route>

				<Route path={AppRoutes.ACCOUNT_CONFIRMATION}>
					<Route
						path={AppRoutes.ACCOUNT_CONFIRMATION}
						element={
							<AuthLayout>
								<SignUpFollowupPage />
							</AuthLayout>
						}
					/>
				</Route>

				<Route path={AppRoutes.ACCOUNT_ACTIVATION}>
					<Route
						path={AppRoutes.ACCOUNT_ACTIVATION}
						element={
							<AuthLayout>
								<ActivatAccountLandingPage />
							</AuthLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.SIGNUP}
					element={
						<IsUserRedirect
							allowed={authService.isAuthenticated(authState.token)}
							loggedInPath={AppRoutes.ROOT}
						/>
					}
				>
					<Route
						path={AppRoutes.SIGNUP}
						element={
							<AuthLayout>
								<SignUp />
							</AuthLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.INVITATION_SIGNUP}
					element={
						<IsUserRedirect
							allowed={authService.isAuthenticated(authState.token)}
							loggedInPath={AppRoutes.ROOT}
						/>
					}
				>
					<Route
						path={AppRoutes.INVITATION_SIGNUP}
						element={
							<AuthLayout>
								<InvitationSignup />
							</AuthLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.CHANGE_PASSWORD}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route
						path={AppRoutes.CHANGE_PASSWORD}
						element={
							<AuthLayout>
								<ChangePasswordView />
							</AuthLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.FORGET_PASSWORD}
					element={
						<IsUserRedirect
							allowed={authService.isAuthenticated(authState.token)}
							loggedInPath={AppRoutes.ROOT}
						/>
					}
				>
					<Route
						path={AppRoutes.FORGET_PASSWORD}
						element={
							<AuthLayout>
								<ForgetPasswordView />
							</AuthLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.PASSWORD_RESET}
					element={
						<IsUserRedirect
							allowed={authService.isAuthenticated(authState.token)}
							loggedInPath={AppRoutes.ROOT}
						/>
					}
				>
					<Route
						path={AppRoutes.PASSWORD_RESET}
						element={
							<AuthLayout>
								<ResetPasswordView />
							</AuthLayout>
						}
					/>
				</Route>

				<Route
					path={AppRoutes.ROOT}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route path={AppRoutes.ROOT} element={<Navigate replace to={AppRoutes.JOBS_DASHBOARD} />} />
				</Route>

				<Route
					path={'/dashboard'}
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route path={'/dashboard'} element={<Navigate replace to={AppRoutes.JOBS_DASHBOARD} />} />
				</Route>

				<Route path={AppRoutes.TERMS_OF_USE} element={<TermsOfUseView />} />

				<Route
					path="*"
					element={
						<ProtectedRoute
							allowed={authService.isAuthenticated(authState.token)}
							activated={authService.isActivated()}
						/>
					}
				>
					<Route path="*" element={<NotFoundRoute />} />
				</Route>
			</Routes>
		</Router>
	);
};
